import { ACTIVE_CARE_PATHWAY_STATUSES, ActiveStatus } from '../types/builder';
import {
  CarePathway,
  CarePathwayFilters,
  CarePathwayStage,
  GetCareGapMatchingQueueReqBody,
  GetCareGapMatchingTabCountsRes,
  GetCareGapScreenedQueueReqBody,
  GetCarePathwayQueueCountsRes,
  GetNextPatientQueryAnswersRes,
  GetPatientCarePathwaysRes,
} from '../types/nextPrescreening';
import type {
  CreatePatientQueryFeedbackRequestDtoType,
  PatientQueryFeedbackSavedDtoType,
  UpdatePatientQueryFeedbackCommentRequestDtoType,
} from '@tempus/trials-types';
import axios from '../axios';
import { NEXT_PRESCREENING, PATIENT_QUERY } from '../constants';

export const nextPrescreeningApi = {
  getCarePathways: async (siteId?: string): Promise<CarePathway[]> => {
    const params: { siteId?: string; statuses: ActiveStatus[] } = { statuses: ACTIVE_CARE_PATHWAY_STATUSES };
    if (siteId) {
      params.siteId = siteId;
    }
    return axios.get(`${NEXT_PRESCREENING}/care-pathways`, { params }).then(({ data }) => data);
  },
  getCarePathwayQueueCounts: async (): Promise<GetCarePathwayQueueCountsRes> => {
    return axios.get(`${NEXT_PRESCREENING}/care-pathways/queue-counts`).then(({ data }) => data);
  },
  getCarePathwayFilters: async (carePathwayId: string): Promise<CarePathwayFilters> => {
    return axios.get(`${NEXT_PRESCREENING}/care-pathway/${carePathwayId}/filters`).then(({ data }) => data);
  },
  getCarePathwayStages: async (carePathwayId: string): Promise<CarePathwayStage[]> => {
    return axios.get(`${NEXT_PRESCREENING}/care-pathway/${carePathwayId}/stages`).then(({ data }) => data);
  },
  getCarePathwayStagesForSite: async (carePathwayId: string, siteId: string): Promise<CarePathwayStage[]> => {
    return axios
      .get(`${NEXT_PRESCREENING}/care-pathway/${carePathwayId}/site/${siteId}/stages`)
      .then(({ data }) => data);
  },
  getCarePathwayTabCounts: async (
    carePathwayId: string,
    body: GetCareGapMatchingQueueReqBody,
  ): Promise<GetCareGapMatchingTabCountsRes> => {
    return axios.post(`${NEXT_PRESCREENING}/care-pathway/${carePathwayId}/queue/count`, body).then(({ data }) => data);
  },
  getPatientCarePathways: async (
    carePathwayId: string,
    body: GetCareGapMatchingQueueReqBody | GetCareGapScreenedQueueReqBody,
  ): Promise<GetPatientCarePathwaysRes> => {
    return axios.post(`${NEXT_PRESCREENING}/care-pathway/${carePathwayId}/queue`, body).then(({ data }) => data);
  },
  /** PQ answers. Keyed by next patient because APQ rows also key on the patient's site. */
  getPatientQueryAnswers: async (
    nextPatientId: string,
    carePathwayStageId: string,
  ): Promise<GetNextPatientQueryAnswersRes> => {
    return axios
      .get(`${PATIENT_QUERY}/next/patients/${nextPatientId}/care-pathway-stages/${carePathwayStageId}/answers`)
      .then(({ data }) => data);
  },
  /** Set (`negative`) or undo (`null`) the APQ answer flag; use `updatePatientQueryAnswerFeedbackComment` for comments. */
  recordPatientQueryAnswerFeedback: async (
    nextPatientId: string,
    promptId: string,
    body: CreatePatientQueryFeedbackRequestDtoType,
  ): Promise<PatientQueryFeedbackSavedDtoType> => {
    return axios
      .post(
        `${PATIENT_QUERY}/feedback/pathways/patients/${encodeURIComponent(nextPatientId)}/prompts/${encodeURIComponent(promptId)}`,
        body,
      )
      .then(({ data }) => data);
  },
  updatePatientQueryAnswerFeedbackComment: async (
    feedbackId: string,
    body: UpdatePatientQueryFeedbackCommentRequestDtoType,
  ): Promise<PatientQueryFeedbackSavedDtoType> => {
    return axios
      .patch(`${PATIENT_QUERY}/feedback/pathways/${encodeURIComponent(feedbackId)}`, body)
      .then(({ data }) => data);
  },
  getQueueCsv: async (
    carePathwayId: string,
    body: Omit<GetCareGapMatchingQueueReqBody, 'page' | 'pageSize'>,
  ): Promise<Blob> => {
    return axios
      .post(`${NEXT_PRESCREENING}/care-pathway/${carePathwayId}/queue/csv`, body, { responseType: 'blob' })
      .then(({ data }) => data);
  },
};
